/* eslint-disable max-len */
import { _n, sprintf } from '@wordpress/i18n';
import {
  getLiveAlertsForSite,
  filterLiveAlertsBySupportedTypes,
  severityIndex,
  sortLiveAlertsBySeverity,
} from 'services/weatherService';

/**
 * Action types.
 */

export const RECEIVE_TEMPLATE_VARIABLES = 'receive_template_variables';

/**
 * Builds and adds either a summary or individual alert banner for the
 * specified alert config. Essentially this logic should handle all the
 * "normal" alert types. The current exception is weather alerts which are
 * being specifically handled.
 *
 * @param alertConfig The alert type configuration which carries the type, copy, and collection to which banners should be added.
 * @param allMessages All alert messages grouped by type.
 * @param allSettings The WP settings for the alert type.
 */
function addAlertBanners(alertConfig, allMessages, allSettings) {
  const bannerType = alertConfig.type;
  const messages = allMessages[bannerType];
  if (0 < messages.length) {
    const settings = allSettings[bannerType];
    const isSummaryOnly = settings.summary_only;
    const formattedMessages = [];
    if (isSummaryOnly && 1 < messages.length) { // Must also have multiple messages in addition to the summary flag.
      formattedMessages.push({
        content: `There are currently ${messages.length} ${alertConfig.copySummary}. Click for more details.`,
        url: settings.url,
      });
    } else {
      messages.forEach(({ content = '', url = '' }) => {
        formattedMessages.push({ content, url });
      });
    }
    // Wrap messages in expected construct and add to collection.
    formattedMessages.forEach((message) => alertConfig.collection.push({
      type: bannerType,
      settings,
      message: message.content,
      url: message.url,
    }));
  }
}

/**
 * Get alert messages from the weather service.
 *
 * @param {object} bannerTypeSettings - Weather Alerts settings.
 * @returns {Promise<*[]>} - A Promise that resolves to an array of data.
 */
const getWeatherAlertMessages = (bannerTypeSettings) => {
  const BANNER_TYPE = 'Weather Alert';
  let totalAreas = 0;

  return getLiveAlertsForSite().then((alertData) => {
    const filteredAlertData = filterLiveAlertsBySupportedTypes(alertData);
    const sortedAlertData = sortLiveAlertsBySeverity(filteredAlertData);

    /**
     * Load the weather alert banner settings.
     */
    const settings = {
      url: '/weather/weather-alerts/',
      label: BANNER_TYPE,
    };

    // Build a keyed map, unique by state and alert headline.
    sortedAlertData.forEach((alert) => {
      const { description, areaId } = alert;
      const collection = {};
      const state = areaId.slice(0, 2);
      const key = `${state}:${description}`;
      const area = alert.areaName.replace(' County', '');

      collection[key] = collection[key] || {
        areas: [],
        severity: '',
        severityCode: 5, // Default to Unknown severity.
      };

      // eslint-disable-next-line space-unary-ops
      if (-1 === collection[key].areas.indexOf(area)) {
        collection[key].areas.push(area);
        collection[key].severity = alert.severity;
        if (
          Object.prototype.hasOwnProperty.call(severityIndex, alert.severity)
        ) {
          collection[key].severityCode = severityIndex[alert.severity];
        }
        totalAreas += 1;
      }
    });

    // Return one or more weather alert messages depending if we are dealing with a summary or not...
    const rawMessages = bannerTypeSettings.summary_only && 1 < sortedAlertData.length // Must also have multiple alerts in addition to the summary flag.
      ? [buildWeatherAlertSummaryMessage(totalAreas, sortedAlertData.length, bannerTypeSettings)]
      : buildWeatherAlertDetailedMessages(sortedAlertData);

    return rawMessages.map((message) => ({
      type: BANNER_TYPE,
      settings,
      message,
      url: settings.url,
    }));
  }).catch(() => []);
};

/**
 * Builds a summary message and is used when "Display number of alerts" aka
 * summary_only has been checked on the alert banner settings. Note that there
 * are also two view options at play: "Number of areas" (default) and
 * "Areas with alerts" which have slightly different copy.
 *
 * @param totalAreas The total number of areas which have alerts.
 * @param numActiveAlerts The total number of alerts.
 * @param bannerTypeSettings The WP banner type settings.
 * @returns {string}
 */
function buildWeatherAlertSummaryMessage(totalAreas, numActiveAlerts, bannerTypeSettings) {
  const totalAreasString = sprintf(
    _n('is %d area', 'are %d areas', totalAreas, 'nexstar'),
    totalAreas,
  );
  // Vary the message by the two view options.
  return 'areas_with_alerts' === bannerTypeSettings.view_options
    // 1) areas_with_alerts
    ? `There ${totalAreasString} with ${sprintf(
      _n(
        '%d active weather alert',
        '%d active weather alerts',
        numActiveAlerts,
        'nexstar-wv',
      ),
      numActiveAlerts,
    )}.`
    // 2) number_of_areas (default)
    : `There ${totalAreasString} under alert. Click for alert details.`;
}

/**
 * Builds multiple weather alert messages.
 * @param sortedAlertData Alerts sorted by severity.
 * @returns {string[]}
 */
function buildWeatherAlertDetailedMessages(sortedAlertData) {
  // Collect alert areas by alert type.
  const areasGroupedByAlertType = {};
  sortedAlertData.forEach((e) => {
    const { alertType, areaName } = e.weatherDetail;
    let areaNames = areasGroupedByAlertType[alertType];
    if (! areaNames) {
      areaNames = [];
      areasGroupedByAlertType[alertType] = areaNames;
    }
    areaNames.push(areaName);
  });
  // Now compose the detailed message.
  return Object.keys(areasGroupedByAlertType).map((alertType) => {
    const areaNames = areasGroupedByAlertType[alertType];
    return `${alertType}: ${areaNames.join(', ')}`;
  });
}

/**
 * Add alert messages to the Template Variables object.
 *
 * @param {Array} messages - Alert banners messages.
 * @param {Object} templateVariables - Template Variables.
 * @returns {Object} - Updated Template Variables.
 */
function addBannersToTemplateVariables(messages, templateVariables) {
  const banners = [
    ...messages.breakingNews,
    ...messages.weather,
    ...messages.closings,
    ...messages.custom,
  ];

  const alertBanners = {
    ...(
      templateVariables?.alert_banners || {} // eslint-disable-line camelcase
    ),
    messages: banners,
    totalLength: banners.length,
  };

  return {
    ...templateVariables,
    alert_banners: alertBanners, // eslint-disable-line camelcase
  };
}

/**
 * Reducer function. Accepts a state and an action, and returns the modified
 * state depending on the action type. If no matching action types are found,
 * returns the unmodified state.
 *
 * @param {object} state - The state to modify or return.
 * @param {object} action - The action type to analyze.
 * @returns {object} - The new state value.
 */
export default function reducer(state = {}, action) {
  switch (action.type) {
    case RECEIVE_TEMPLATE_VARIABLES:
      return action.variables;

    default:
      return state;
  }
}

/**
 * Fires when receiving updated template variables for the page.
 *
 * @param variables
 * @returns {{variables: {}, type: string}}
 */
export function receiveTemplateVariables(variables = {}) {
  return {
    type: RECEIVE_TEMPLATE_VARIABLES,
    variables,
  };
}

/**
 * Thunk for getting the current template variables.
 */
export function updateTemplateVariables() {
  const { root } = window.nsApiSettings;

  /**
   * Note that in effect we are requesting the variables for the home page no
   * matter what page is being viewed. This works in practice because the only
   * data currently needed are sitewide variables, and it allows the same cached
   * endpoint to be used across the site. In the future, the path requested here
   * could include the page-specific query vars to get the template variables
   * specific to the current page instead of always the home page variables. Or,
   * the store could contain separate keys for sitewide and page-specific
   * template variables built from two API requests.
   */
  let templateVariablesUrl = 'template-variables/';
  if ('alert_banners' in window.NXSTdata.alerts) {
    templateVariablesUrl = 'template-variables/?filter_variables=alert_banners';
  }

  return (dispatch) => fetch(`${root}lakana/v1/${templateVariablesUrl}`)
    .then((response) => {
      if (! response.ok) {
        throw new Error('Network response not ok');
      }

      return response.json();
    })
    .then((json) => {
      const templateVariables = json;
      const allMessages = templateVariables.alert_banners.messages;
      const allSettings = templateVariables.alert_banners.settings;
      const formattedMessages = {
        breakingNews: [],
        weather: [],
        closings: [],
        custom: [],
      };

      // Handle Breaking News, Closings, and Custom alerts (purposefully ignoring Live Now).
      [
        {
          type: 'Breaking News',
          copySummary: 'breaking news alerts',
          collection: formattedMessages.breakingNews,
        },
        {
          type: 'Closings',
          copySummary: 'active closings',
          collection: formattedMessages.closings,
        },
        {
          type: 'Custom',
          copySummary: 'alerts',
          collection: formattedMessages.custom,
        }].forEach((alertConfig) => addAlertBanners(alertConfig, allMessages, allSettings));

      return getWeatherAlertMessages(allSettings['Weather Alerts'])
        .then((weatherAlertMessages) => {
          if (Array.isArray(weatherAlertMessages)) {
            formattedMessages.weather.push(...weatherAlertMessages);
          }

          return addBannersToTemplateVariables(formattedMessages, templateVariables);
        })
        .catch(() => addBannersToTemplateVariables(formattedMessages, templateVariables));
    })
    .then((json) => dispatch(receiveTemplateVariables(json)))
    .catch(() => {} /* silence */);
}
